import { useEffect, useRef, useState } from "react";
import { MapContainer, Polyline, CircleMarker, useMap } from "react-leaflet";
import MapTiles from "../components/MapTiles";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Square, Pause, Navigation2, AlertTriangle, Footprints, Zap } from "lucide-react";
import api from "../lib/api";
import { haversine, fmtDistance, fmtDuration, fmtPace, todayStr } from "../lib/format";
import { getCurrentPosition, requestTrackingPermissions, startTracking, stopTracking, updateTrackingStats } from "../lib/tracking";

// How often the pace figure is recomputed while recording.
const PACE_REFRESH_MS = 5000;

function Recenter({ point, follow }) {
  const map = useMap();
  useEffect(() => {
    if (point && follow) map.setView([point.lat, point.lng], map.getZoom(), { animate: true });
  }, [point, follow, map]);
  return null;
}

export default function Record() {
  const [status, setStatus] = useState("idle"); // idle | running | paused | saving
  const [route, setRoute] = useState([]);
  const [distance, setDistance] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [current, setCurrent] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [activity, setActivity] = useState("run"); // run | walk
  // Average pace (min/km) over the run so far. Refreshed on an interval
  // rather than every tick — the value barely moves between updates and a
  // number re-rendering four times a second is just noise while running.
  const [pace, setPace] = useState(0);

  const trackingHandle = useRef(null);
  const timerRef = useRef(null);
  const startedAt = useRef(null);
  const lastPoint = useRef(null);
  const statusRef = useRef("idle");
  const wakeLock = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Get an initial position on mount for map centering
  useEffect(() => {
    getCurrentPosition()
      .then(setCurrent)
      .catch(() => {});
    return () => {
      stopTiming();
      stopTracking(trackingHandle.current);
      trackingHandle.current = null;
    };
    // eslint-disable-next-line
  }, []);

  const stopTiming = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const acquireWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) wakeLock.current = await navigator.wakeLock.request("screen");
    } catch (e) {
      /* ignore */
    }
  };

  const releaseWakeLock = () => {
    try {
      if (wakeLock.current) wakeLock.current.release();
    } catch (e) {}
    wakeLock.current = null;
  };

  const distanceRef = useRef(0);
  const lastStatsPush = useRef(0);
  const paceRef = useRef(0); // mirrors pace so the timer can read it directly
  const lastPaceUpdate = useRef(0);
  // Per-kilometer splits, closed off as each boundary is crossed and saved
  // with the run. Only recorded here — the live pace figure stays an average.
  const splits = useRef([]);
  const lastSplit = useRef({ km: 0, elapsedS: 0 });

  const recordSplits = (distanceM) => {
    const completedKm = Math.floor(distanceM / 1000);
    if (completedKm <= lastSplit.current.km) return;

    const elapsedS = Math.floor((Date.now() - startedAt.current) / 1000);
    const kmGained = completedKm - lastSplit.current.km;
    // A single fix can straddle more than one boundary after a GPS gap;
    // spread that stretch evenly over the kilometers it covered.
    const perKmS = (elapsedS - lastSplit.current.elapsedS) / kmGained;
    for (let i = 1; i <= kmGained; i++) {
      splits.current.push({
        km: lastSplit.current.km + i,
        duration_s: perKmS,
        distance_m: 1000,
      });
    }
    lastSplit.current = { km: completedKm, elapsedS };
  };

  // Splits plus the unfinished last kilometer, for saving with the run.
  const buildSplits = (totalDistanceM, totalElapsedS) => {
    const all = [...splits.current];
    const restM = totalDistanceM - lastSplit.current.km * 1000;
    const restS = totalElapsedS - lastSplit.current.elapsedS;
    if (restM > 10 && restS > 0) {
      all.push({ km: lastSplit.current.km + 1, duration_s: restS, distance_m: restM });
    }
    return all;
  };

  const onPosition = (pos) => {
    const p = { lat: pos.lat, lng: pos.lng };
    setAccuracy(pos.accuracy);
    setCurrent(p);
    if (statusRef.current !== "running") return;
    // ignore very inaccurate points
    if (pos.accuracy && pos.accuracy > 40) return;
    if (lastPoint.current) {
      const d = haversine(lastPoint.current, p);
      if (d > 1.5) {
        const next = distanceRef.current + d;
        distanceRef.current = next;
        setDistance(next);
        setRoute((prev) => [...prev, p]);
        lastPoint.current = p;
        recordSplits(next);
      }
    } else {
      lastPoint.current = p;
      setRoute((prev) => [...prev, p]);
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      const elapsedS = Math.floor((Date.now() - startedAt.current) / 1000);
      setElapsed(elapsedS);

      const now = Date.now();
      if (now - lastPaceUpdate.current >= PACE_REFRESH_MS) {
        lastPaceUpdate.current = now;
        const d = distanceRef.current;
        paceRef.current = d > 0 ? elapsedS / 60 / (d / 1000) : 0;
        setPace(paceRef.current);
      }

      // Time and distance tick every second on the lock screen / in the
      // notification; pace rides along with whatever the last refresh produced.
      if (now - lastStatsPush.current >= 1000) {
        lastStatsPush.current = now;
        updateTrackingStats({
          distanceLabel: fmtDistance(distanceRef.current),
          durationLabel: fmtDuration(elapsedS),
          paceLabel: fmtPace(paceRef.current),
        });
      }
    }, 250);
  };

  const handleStart = async () => {
    const granted = await requestTrackingPermissions();
    if (!granted) {
      toast.error("Standortfreigabe wird für die Aufzeichnung benötigt.");
      return;
    }
    setGpsError(null);
    setRoute([]);
    setDistance(0);
    distanceRef.current = 0;
    setElapsed(0);
    lastPoint.current = null;
    lastStatsPush.current = 0;
    setPace(0);
    paceRef.current = 0;
    lastPaceUpdate.current = 0;
    splits.current = [];
    lastSplit.current = { km: 0, elapsedS: 0 };
    startedAt.current = Date.now();
    setStatus("running");
    statusRef.current = "running";
    trackingHandle.current = await startTracking({
      activityType: activity,
      onPosition,
      onError: (message) => setGpsError(message),
    });
    startTimer();
    acquireWakeLock();
    toast.success(activity === "run" ? "Lauf gestartet – viel Spaß!" : "Spaziergang gestartet!");
  };

  const handlePause = () => {
    setStatus("paused");
    statusRef.current = "paused";
    if (timerRef.current) clearInterval(timerRef.current);
    lastPoint.current = null; // avoid a big jump on resume
  };

  const handleResume = () => {
    startedAt.current = Date.now() - elapsed * 1000;
    setStatus("running");
    statusRef.current = "running";
    startTimer();
  };

  const handleStop = async () => {
    setStatus("saving");
    statusRef.current = "saving";
    stopTiming();
    await stopTracking(trackingHandle.current);
    trackingHandle.current = null;
    releaseWakeLock();
    if (distance < 5 || elapsed < 3) {
      toast.error("Zu kurz zum Speichern.");
      setStatus("idle");
      statusRef.current = "idle";
      return;
    }
    try {
      await api.post("/runs", {
        distance_m: distance,
        duration_s: elapsed,
        route,
        splits: buildSplits(distance, elapsed),
        type: activity,
        day: todayStr(),
        started_at: new Date(startedAt.current).toISOString(),
      });
      toast.success(activity === "run" ? "Lauf gespeichert!" : "Spaziergang gespeichert!");
      navigate("/history");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen.");
      setStatus("idle");
      statusRef.current = "idle";
    }
  };

  const centerPoint = current || { lat: 52.52, lng: 13.405 };
  const running = status === "running";

  return (
    <div className="relative h-[calc(100vh-0px)] overflow-hidden">
      {/* Map */}
      <div className="absolute inset-0 dark-map">
        <MapContainer
          center={[centerPoint.lat, centerPoint.lng]}
          zoom={16}
          zoomControl={false}
          attributionControl={true}
          style={{ height: "100%", width: "100%" }}
        >
          <MapTiles />
          {route.length > 1 && (
            <Polyline positions={route.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#CCFF00", weight: 5 }} />
          )}
          {current && (
            <CircleMarker
              center={[current.lat, current.lng]}
              radius={8}
              pathOptions={{ color: "#0A0A0A", fillColor: "#CCFF00", fillOpacity: 1, weight: 3 }}
            />
          )}
          <Recenter point={current} follow={running} />
        </MapContainer>
      </div>

      {/* Top bar */}
      <div className="absolute top-0 left-0 w-full z-[500] px-4 pb-4 top-safe flex items-center justify-between pointer-events-none">
        <div className="backdrop-blur-xl bg-black/60 border border-white/10 rounded-full px-4 py-2 flex items-center gap-2">
          <span className={`relative w-2.5 h-2.5 rounded-full ${running ? "bg-[#CCFF00] ping-dot" : "bg-neutral-500"}`} />
          <span className="text-xs font-bold uppercase tracking-wider">
            {status === "idle" && "Bereit"}
            {running && "Aufzeichnung"}
            {status === "paused" && "Pausiert"}
            {status === "saving" && "Speichern"}
          </span>
        </div>
        {accuracy != null && (
          <div className="backdrop-blur-xl bg-black/60 border border-white/10 rounded-full px-3 py-2 flex items-center gap-1.5">
            <Navigation2 className="w-3.5 h-3.5 text-[#CCFF00]" />
            <span className="text-xs text-neutral-300">±{Math.round(accuracy)}m</span>
          </div>
        )}
      </div>

      {/* GPS error */}
      {gpsError && (
        <div className="absolute top-20 left-4 right-4 z-[500] backdrop-blur-xl bg-black/80 border border-[#FF3366]/40 rounded-2xl p-4 flex items-start gap-3" data-testid="gps-error">
          <AlertTriangle className="w-5 h-5 text-[#FF3366] shrink-0 mt-0.5" />
          <p className="text-sm text-neutral-200">{gpsError}</p>
        </div>
      )}

      {/* Bottom sheet */}
      <div className="absolute bottom-0 left-0 w-full z-[500] rounded-t-3xl bg-[#0A0A0A] border-t border-white/10 px-6 pt-6 pb-28">
        {status === "idle" && (
          <div className="flex items-center gap-2 mb-6 bg-[#121212] border border-white/10 rounded-full p-1" data-testid="activity-toggle">
            <button
              data-testid="activity-run"
              onClick={() => setActivity("run")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold transition-colors ${
                activity === "run" ? "bg-[#CCFF00] text-black" : "text-neutral-400"
              }`}
            >
              <Zap className="w-4 h-4" /> Lauf
            </button>
            <button
              data-testid="activity-walk"
              onClick={() => setActivity("walk")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold transition-colors ${
                activity === "walk" ? "bg-[#CCFF00] text-black" : "text-neutral-400"
              }`}
            >
              <Footprints className="w-4 h-4" /> Spaziergang
            </button>
          </div>
        )}
        {status !== "idle" && (
          <div className="mb-4 flex items-center gap-2">
            {activity === "run" ? <Zap className="w-4 h-4 text-[#CCFF00]" /> : <Footprints className="w-4 h-4 text-[#CCFF00]" />}
            <span className="data-label">{activity === "run" ? "Lauf" : "Spaziergang"}</span>
          </div>
        )}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div>
            <div className="data-label mb-1">Zeit</div>
            <div data-testid="live-time" className="font-display font-black text-3xl tracking-tighter tabular-nums">
              {fmtDuration(elapsed)}
            </div>
          </div>
          <div>
            <div className="data-label mb-1">Distanz km</div>
            <div data-testid="live-distance" className="font-display font-black text-3xl tracking-tighter tabular-nums text-[#CCFF00]">
              {fmtDistance(distance)}
            </div>
          </div>
          <div>
            <div className="data-label mb-1">Pace /km</div>
            <div data-testid="live-pace" className="font-display font-black text-3xl tracking-tighter tabular-nums">
              {fmtPace(pace)}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-6">
          {status === "idle" && (
            <button
              data-testid="start-run-button"
              onClick={handleStart}
              className="w-24 h-24 rounded-full bg-[#CCFF00] flex items-center justify-center active:scale-95 transition-transform shadow-[0_0_40px_rgba(204,255,0,0.4)]"
            >
              <Play className="w-10 h-10 text-black ml-1" fill="black" />
            </button>
          )}
          {running && (
            <>
              <button
                data-testid="pause-run-button"
                onClick={handlePause}
                className="w-16 h-16 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
              >
                <Pause className="w-7 h-7 text-white" fill="white" />
              </button>
              <button
                data-testid="stop-run-button"
                onClick={handleStop}
                className="w-24 h-24 rounded-full bg-[#FF3366] flex items-center justify-center active:scale-95 transition-transform shadow-[0_0_40px_rgba(255,51,102,0.4)]"
              >
                <Square className="w-9 h-9 text-white" fill="white" />
              </button>
            </>
          )}
          {status === "paused" && (
            <>
              <button
                data-testid="resume-run-button"
                onClick={handleResume}
                className="w-24 h-24 rounded-full bg-[#CCFF00] flex items-center justify-center active:scale-95 transition-transform"
              >
                <Play className="w-10 h-10 text-black ml-1" fill="black" />
              </button>
              <button
                data-testid="stop-run-button"
                onClick={handleStop}
                className="w-16 h-16 rounded-full bg-[#FF3366] flex items-center justify-center active:scale-95 transition-transform"
              >
                <Square className="w-7 h-7 text-white" fill="white" />
              </button>
            </>
          )}
          {status === "saving" && (
            <div className="w-24 h-24 rounded-full border-2 border-[#CCFF00] border-t-transparent animate-spin" />
          )}
        </div>
      </div>
    </div>
  );
}
